import React, { useState, useEffect, useMemo, useRef } from "react";
import { CSVLink } from "react-csv";
import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import MultiSelectFilter from "../../shared/MultiSelectFilter";
import { formatSpentValue } from "../../utils/constants";

const formatLocationLabel = (name = "") =>
  String(name)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim() || "Other";

const getMonthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonthHeader = (yearMonth) => {
  const [y, m] = yearMonth.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  const month = d.toLocaleString("default", { month: "short" });
  const year = String(y).slice(-2);
  return `${month}'${year}`;
};

function SalesPersonWiseReport({ dateRange, initiateDownloadCSV, setInitiateDownloadCSV, selectedSalesPersonId }) {
  const [salesData, setSalesData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchData = async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      setSalesData([]);
      return;
    }
    try {
      setLoader(true);
      const response = await API({
        url: "/alpha/get-salesperson-month-wise-reports",
        method: "POST",
        responseType: "json",
        data: { dateRange, sales_person_id: +selectedSalesPersonId || undefined },
      });
      if (response?.code === 200) {
        setSalesData(Array.isArray(response.data) ? response.data : []);
      } else {
        setSalesData([]);
      }
    } catch (error) {
      console.error("Error fetching sales person data", error);
      setSalesData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedSalesPersonId]);

  const monthColumns = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];
    const start = new Date(dateRange[0]);
    const end = new Date(dateRange[1]);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setDate(1);
    end.setHours(0, 0, 0, 0);
    const months = [];
    for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
      months.push(getMonthKey(d));
    }
    return months;
  }, [dateRange]);

  const toMonthKey = (yearMonth) => {
    if (typeof yearMonth === "number") {
      return getMonthKey(new Date(yearMonth * 1000));
    }
    return yearMonth;
  };

  const salesPersonRows = useMemo(() => {
    const seen = new Map();
    (salesData || []).forEach((record) => {
      const locKey = String(record.location ?? "").trim() || "-1";
      const locName = formatLocationLabel(record.location_name || "");
      const spKey = `sp-${record.sales_person_id}-${locKey}`;
      if (!seen.has(spKey)) {
        seen.set(spKey, {
          key: spKey,
          type: "salesperson",
          sales_person_id: record.sales_person_id,
          sales_person_name: record.sales_person_name || "",
          location: locKey,
          location_name: locName,
          label: locName
            ? `${locName} - ${record.sales_person_name || ""}`
            : `${record.sales_person_name || ""}`,
        });
      }
    });
    return Array.from(seen.values());
  }, [salesData]);

  const visibleSalesPersonRows = useMemo(
    () => (selectedRowKeys.length ? salesPersonRows.filter((r) => selectedRowKeys.includes(r.key)) : salesPersonRows),
    [salesPersonRows, selectedRowKeys]
  );

  const rowsWithLocationHeaders = useMemo(() => {
    const byLocation = new Map();
    visibleSalesPersonRows.forEach((row) => {
      const loc = row.location;
      const locName = row.location_name;
      if (!byLocation.has(loc)) {
        byLocation.set(loc, {
          location: loc,
          locationName: locName,
          salesPersons: [],
        });
      }
      byLocation.get(loc).salesPersons.push(row);
    });

    let locations = Array.from(byLocation.entries())
      .sort((a, b) => {
        const nameA = a[1].locationName.toLowerCase();
        const nameB = b[1].locationName.toLowerCase();
        if (nameA === "other") return 1;
        if (nameB === "other") return -1;
        return nameA.localeCompare(nameB);
      })
      .map(([loc, info]) => ({ location: loc, ...info }));
    let toBeLastColsKeyWords = ['classified', 'inbound']
    let lastCols = locations.filter((col) => toBeLastColsKeyWords.some((keyword) => col.locationName?.toLowerCase().includes(keyword)));
    let firstCols = locations.filter((col) => !lastCols.includes(col));
    locations = [...firstCols, ...lastCols];
    const result = [];
    locations.forEach(({ location, locationName, salesPersons }) => {
      result.push({
        key: `loc-${location}`,
        type: "location",
        label: locationName,
        location,
        isHeader: true,
      });
      salesPersons
        .sort((a, b) =>
          String(a.sales_person_name).localeCompare(String(b.sales_person_name))
        )
        .forEach((sp) => result.push({ ...sp, isHeader: false }));
    });
    return result;
  }, [visibleSalesPersonRows]);

  const dataBySalesPersonMonth = useMemo(() => {
    const map = {};
    (salesData || []).forEach((record) => {
      const locKey = String(record.location ?? "").trim() || "-1";
      const rowKey = `sp-${record.sales_person_id}-${locKey}`;
      const monthKey = toMonthKey(record.yearMonth);
      if (!map[rowKey]) map[rowKey] = {};
      if (!map[rowKey][monthKey]) map[rowKey][monthKey] = { ads: 0, spent: 0 };
      map[rowKey][monthKey].ads += Number(record.totalAds || 0);
      map[rowKey][monthKey].spent += Number(record.totalSpent || 0);
    });
    return map;
  }, [salesData]);

  const dataByLocationMonth = useMemo(() => {
    const map = {};
    rowsWithLocationHeaders.forEach((row) => {
      if (row.type !== "salesperson") return;
      const locKey = `loc-${row.location}`;
      if (!map[locKey]) map[locKey] = {};
      monthColumns.forEach((monthKey) => {
        const cell = dataBySalesPersonMonth[row.key]?.[monthKey];
        if (cell) {
          if (!map[locKey][monthKey]) map[locKey][monthKey] = { ads: 0, spent: 0 };
          map[locKey][monthKey].ads += cell.ads;
          map[locKey][monthKey].spent += cell.spent;
        }
      });
    });
    return map;
  }, [rowsWithLocationHeaders, dataBySalesPersonMonth, monthColumns]);

  const grandTotalsByMonth = useMemo(() => {
    const totals = {};
    monthColumns.forEach((monthKey) => {
      totals[monthKey] = { ads: 0, spent: 0 };
      visibleSalesPersonRows.forEach((row) => {
        const cell = dataBySalesPersonMonth[row.key]?.[monthKey];
        totals[monthKey].ads += cell?.ads || 0;
        totals[monthKey].spent += cell?.spent || 0;
      });
    });
    return totals;
  }, [monthColumns, visibleSalesPersonRows, dataBySalesPersonMonth]);

  const getCellForRow = (row, monthKey) => {
    if (row.type === "location") {
      return dataByLocationMonth[row.key]?.[monthKey] || { ads: 0, spent: 0 };
    }
    return dataBySalesPersonMonth[row.key]?.[monthKey] || { ads: 0, spent: 0 };
  };

  const colspan = 1 + monthColumns.length * 2;

  const handleDownloadCSV = () => {
    if (!salesPersonRows.length || !monthColumns.length) return;
    // Export always covers every sales person, independent of the on-screen row filter.
    const sortedRows = [...salesPersonRows].sort((a, b) => {
      const locA = (a.location_name || "").toLowerCase();
      const locB = (b.location_name || "").toLowerCase();
      if (locA !== locB) {
        if (locA === "other") return 1;
        if (locB === "other") return -1;
        return locA.localeCompare(locB);
      }
      return String(a.sales_person_name).localeCompare(String(b.sales_person_name));
    });
    const csvRows = sortedRows.map((row) => {
      const obj = { "Sales POC": row.label };
      monthColumns.forEach((monthKey) => {
        const cell = getCellForRow(row, monthKey);
        obj[`${formatMonthHeader(monthKey)} - Count of Ads`] = cell.ads ? cell.ads : "";
        obj[`${formatMonthHeader(monthKey)} - Revenue (Excl GST)`] = cell.spent ?? "";
      });
      return obj;
    });
    // Grand totals recomputed across the full, unfiltered sales person set —
    // decoupled from grandTotalsByMonth, which now reflects the row filter.
    const csvGrandTotalsByMonth = {};
    monthColumns.forEach((monthKey) => {
      csvGrandTotalsByMonth[monthKey] = { ads: 0, spent: 0 };
      salesPersonRows.forEach((row) => {
        const cell = dataBySalesPersonMonth[row.key]?.[monthKey];
        csvGrandTotalsByMonth[monthKey].ads += cell?.ads || 0;
        csvGrandTotalsByMonth[monthKey].spent += cell?.spent || 0;
      });
    });
    const totalRow = {
      "Sales POC": "Grand Total",
      ...Object.fromEntries(
        monthColumns.flatMap((m) => {
          const t = csvGrandTotalsByMonth[m] || { ads: 0, spent: 0 };
          return [
            [`${formatMonthHeader(m)} - Count of Ads`, t.ads ?? ""],
            [`${formatMonthHeader(m)} - Revenue (Excl GST)`, t.spent ?? ""],
          ];
        })
      ),
    };
    setCsvData([...csvRows, totalRow]);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);

  useEffect(() => {
    if (initiateDownloadCSV) {
      console.log("initiateDownloadCSV");
      handleDownloadCSV();
      setInitiateDownloadCSV(false);
    }
  }, [initiateDownloadCSV]);

  const csvFilename = `sales_person_wise_report_${dateRange?.[0] ? new Date(dateRange[0]).toISOString().slice(0, 10) : "start"}_${dateRange?.[1] ? new Date(dateRange[1]).toISOString().slice(0, 10) : "end"}.csv`;

  return (
    <div className="alpha-report alpha-report-sales-person-wise">
      <CSVLink data={csvData} filename={csvFilename} className="d-none" ref={csvLinkRef} />
      <MultiSelectFilter
        label="Sales POC"
        options={salesPersonRows.map((r) => ({ value: r.key, label: r.label }))}
        selected={selectedRowKeys}
        onChange={setSelectedRowKeys}
      />
      <div className="table-responsive alpha-report-table-wrap">
        <table className="table table-bordered table-sm align-middle alpha-report-table">
          <colgroup>
            <col style={{ width: "var(--sticky-width)" }} />
            {monthColumns.flatMap((m) => [
              <col key={`${m}-ads`} style={{ width: 70 }} />,
              <col key={`${m}-spent`} style={{ width: 70 }} />,
            ])}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="alpha-report-th-sticky-first">Sales POC</th>
              {monthColumns.map((monthKey) => (
                <th key={monthKey} colSpan={2} className="text-center alpha-report-th-month" style={{ minWidth: 140 }}>
                  {formatMonthHeader(monthKey)}
                </th>
              ))}
            </tr>
            <tr>
              {monthColumns.map((monthKey) => (
                <React.Fragment key={`sub-${monthKey}`}>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>Count of <br /> Ads</th>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>Revenue <br /> (Excl GST)</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          {loader ? (
            <TableLoader colspan={colspan} />
          ) : !rowsWithLocationHeaders.length ? (
            <NoDataFound colspan={colspan} />
          ) : (
            <tbody>
              {rowsWithLocationHeaders.map((row) => (
                <tr key={row.key} className={row.isHeader ? "fw-bold" : ""}>
                  <td
                    title={row.label}
                    className={`alpha-report-td-sticky-first alpha-report-td-sticky-first-wrap ${row.isHeader ? "bg-light" : ""}`}
                  >
                    {row.label}
                  </td>
                  {monthColumns.map((monthKey) => {
                    const cell = getCellForRow(row, monthKey);
                    return (
                      <React.Fragment key={`${row.key}-${monthKey}`}>
                        <td className="text-end" style={{ minWidth: 70 }}>
                          {cell.ads ? cell.ads : ""}
                        </td>
                        <td className="text-end" style={{ minWidth: 70 }} title={cell.spent}>
                          {cell.spent ? formatSpentValue(cell.spent) : ""}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
              <tr className="fw-bold">
                <td className="alpha-report-td-grand-total-sticky">Grand Total</td>
                {monthColumns.map((monthKey) => {
                  const t = grandTotalsByMonth[monthKey] || {
                    ads: 0,
                    spent: 0,
                  };
                  return (
                    <React.Fragment key={`total-${monthKey}`}>
                      <td className="text-end alpha-report-td-grand-total">{t.ads}</td>
                      <td className="text-end alpha-report-td-grand-total" title={t.spent}>
                        {t.spent ? formatSpentValue(t.spent) : ""}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

export default SalesPersonWiseReport;
